<template>
  <div class="ma-3" v-if="device.isReady()">
    <v-row class="justify-left mb-5" v-if="device.hasFeature('services.cloud.metrics.enabled') || isDeviceAdmin">
      <videon-chart-tile 
        :device="device"
        heading="CPU Utilization"
        icon="memory"
        :metrics="['cpu_percent_used']"
        unit="%"
        :thresholdWarning="70"
        :thresholdCritical="90"
      />
      
      <videon-chart-tile 
        :device="device"
        heading="Memory Utilization"
        icon="memory"
        :metrics="['memory_system_percent_used']"
        :fill="true"
        unit="%"
        :thresholdWarning="70"
        :thresholdCritical="90"
      />
      
      <videon-chart-tile 
        :device="device"
        heading="Network Output"
        icon="settings_ethernet"
        :metrics="['network_eth0_bytes_sent_per_second']"
        unit="bytes"
      />
      
      <videon-chart-tile 
        :device="device"
        heading="Storage Utilization"
        icon="save"
        :metrics="['filesystem_/data_percent_used']"
        :fill="true"
        unit="%"
        :thresholdWarning="70"
        :thresholdCritical="90"
        v-if="productName == 'edgecaster'"
      />
      
      <videon-chart-tile 
        :device="device"
        heading="Storage Utilization"
        icon="save"
        :metrics="['filesystem_/persist_percent_used']"
        :fill="true"
        unit="%"
        :thresholdWarning="70"
        :thresholdCritical="90"
        v-else-if="productName == 'edgecaster_max'"
      />
      
      <videon-chart-tile 
        :device="device"
        heading="Temperature"
        icon="device_thermostat"
        :metrics="['thermal_som_temp_current']"
        unit="°C"
        :thresholdWarning="80"
        :thresholdCritical="90"
        v-if="productName == 'edgecaster'"
      />
      
      <videon-chart-tile 
        :device="device"
        heading="Temperature"
        icon="device_thermostat"
        :metrics="['thermal_video-usr_temp_current']"
        unit="°C"
        :thresholdWarning="80"
        :thresholdCritical="90"
        v-else-if="productName == 'edgecaster_max'"
      />
    </v-row>
    
    <div v-if="device.state" class="ma-2">
      <v-row><h5 class="text--disabled">Configuration State</h5></v-row>
      <v-row><v-divider /></v-row>
      
      <device-monitor-view :device="device" class="mt-5" />
      
      <v-row class="mt-3"><h5 class="text--disabled">Device Info</h5></v-row>
      <v-row><v-divider /></v-row>
      <v-row>
        <videon-state-tile
          heading="Device Name"
        >
          {{ device.deviceName() }}
        </videon-state-tile>
        <videon-state-tile
          heading="Platform"
        >
          <product-label :device="device"/>
        </videon-state-tile>
        <videon-state-tile
          heading="Serial Number"
        >
          {{ device.state.serial_number }}
        </videon-state-tile>
        
        <videon-state-tile
          heading="Manufacture Date"
          class="secondary--text"
          v-if="isAdmin && device.manufacture_date"
        >
          {{ device.manufacture_date }}
        </videon-state-tile>
        
        <videon-state-tile
          heading="Firmware Version"
        >
          <firmware-version-label :device="device"/>
        </videon-state-tile>
        <videon-state-tile
          heading="MAC Address"
        >
          {{ device.state.mac_address_current }}
        </videon-state-tile>
        
        <videon-state-tile
          heading="Last Boot"
        >
          {{ device.state.last_boot | moment("YYYY/MM/DD hh:mm:ss a") }}
        </videon-state-tile>
        <videon-state-tile
          heading="Device Uptime"
        >
          {{ uptime }}
        </videon-state-tile>
        
        
        <videon-state-tile
          heading="Last State Update"
        >
          {{ device.state.last_state_update | moment("YYYY/MM/DD hh:mm:ss a") }}
        </videon-state-tile>
        
        <videon-state-tile
          heading="Last API Request ID"
          class="text--disabled"
          v-if="isAdmin && debug"
        >
          {{ device.last_api_request_id }}
        </videon-state-tile>
      </v-row>
      
      <v-row><h5 class="text--disabled">Device State</h5></v-row>
      <v-row><v-divider /></v-row>
      <v-row>
        <videon-state-tile
          heading="Device Online"
          class="text-capitalize"
        >
          <span 
            :class="(!device.isOnline()) ? 'error--text' : ''"
          >
            {{ device.isOnline() }}
          </span>
        </videon-state-tile>
        <videon-state-tile
          heading="Ready For Commands"
          class="text-capitalize"
        >
          <span 
            :class="(!device.state.ready_for_commands) ? 'error--text' : ''"
          >
            {{ device.state.ready_for_commands }}
          </span>
        </videon-state-tile>
        <videon-state-tile
          heading="Config Ready"
          class="text-capitalize"
        >
          <span
            :class="(!device.configReadyDisplay()) ? 'error--text' : ''"
          >
            {{ device.configReadyDisplay() }}
          </span>
        </videon-state-tile>
        <videon-state-tile
          heading="Internal IP"
        >
          {{ device.deviceIP() }}
          <span v-if="device.IPScheme() == 'dhcp'" class="font-weight-thin">(DHCP)</span>
        </videon-state-tile>
        <videon-state-tile
          heading="External IP"
        >
          {{ externalIP }}
        </videon-state-tile>
        <videon-state-tile
          heading="Processor State"
        >
          <processors-status-label :device="device"/>
        </videon-state-tile>
      </v-row>

      <device-fleets-section :device_guid="device_guid" />
      
    </div>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex'
  import moment from 'moment'
  
  import DeviceMonitorView from '../DeviceMonitorView.vue'
  import DeviceFleetsSection from './DeviceFleetsSection.vue'
  
  export default {
    name: 'EncoderInfo',
    components: {
      DeviceMonitorView,
      DeviceFleetsSection,
    },
    
    props: ['device_guid', 'device'],
    
    data() {
      return {
        debug: false,
        inputPreviewSupportedVersion: this.$helpers.parseVersion('cloud', 'v1.3.0'),
      }
    },
    
    mounted() {
      setTimeout(() => {
        this.device.fetchMetrics()
      }, 500)
      
    },
    
    computed: {
      ...mapGetters('user', ['user', 'guid', 'isAdmin', 'isDeviceAdmin', 'organization']),
      
      loading() {
        if (this.device && !this.device.ready) {
          return true
        }
        return false
      },
      online() {
        return this.device.isOnline()
      },
      icon() {
        if (this.device) {
          return this.device.icon()
        }
        return ''
      },
      
      deviceName() {
        if (this.device) {
          return this.device.deviceName()
        }
        
        return this.device_guid
      },
      
      productName() {
        if (this.device) {
          return this.device.product_name
        }
        
        return 'edgecaster'
      },
      
      externalIP() {
        if (this.device && this.device.external_ip) {
          return this.device.external_ip
        }
        return 'Unknown'
      },
      
      uptime() {
        if (!this.device.isOnline()) {
          return 'Offline'
        } else if (this.device && this.device.state) {
          if (this.device.state.last_boot && this.device.last_api_request_id) {
            return moment(this.device.state.last_boot).fromNow(true)
          }
        }
        return 'Unknown'
      },
      
      inputsState() {
        return this.device.inputsState()
      },
      
      outputsState() {
        return this.device.outputsState()
      },
    },
    methods: {
      inputName(shadowDocument) {
        if (shadowDocument.config.name) {
          return this.$helpers.htmlDecode(shadowDocument.config.name)
        }
        
        return 'Input #' + shadowDocument.id
      },
      outputName(shadowDocument) {
        if (shadowDocument.config.name) {
          return this.$helpers.htmlDecode(shadowDocument.config.name)
        }
        
        var outputName = 'Output #' + shadowDocument.id
        if (shadowDocument.type) {
          outputName += ' (' + this.$helpers.getLabel(shadowDocument.type) + ')'
        }
        return outputName
      },
      isPreviewSupported(index = 0) {
        if (this.device.thumbnailEnabled(index) && this.$helpers.versionCheck(this.inputPreviewSupportedVersion, this.device.cloud_version)) {
          return true
        }
        return false
      }
    }
  }
</script>